/**
 * Sprites de los avatares, generados por código.
 *
 * Cada avatar es un muñeco articulado de perfil: una pose son ángulos de
 * cadera, rodilla, hombro y codo. Los cuadros se pintan una vez por persona
 * en una rejilla lógica y se guardan en caché.
 */
import { Grid, INK, PX, hashId, shade } from './pixelKit';

const W = 46;
const H = 58;
const OX = 20;
const OY = 56;
const THIGH = 8;
const SHIN = 8;
const TORSO = 11;
const ARM = 6;

const SHIRTS = ['#c45c48', '#3d6b9a', '#d4893a', '#4a7c59', '#7a4ea0', '#2f6f73', '#b8475f', '#5c6bc0', '#e0b23c', '#6d8f3a'];
const HAIR = ['#1a120c', '#4a2c18', '#c4a574', '#2b1a12', '#8a4a22', '#d9d2c4', '#3a2218', '#a13d2d'];
const SKIN = ['#f0c09a', '#e0ac7a', '#c68642', '#8d5524', '#f6d7b8', '#d9a066'];
const PANTS = ['#3a3f55', '#4a3728', '#2c2c38', '#5c4a3a', '#2f4a5c', '#6a6f7a'];
const SHOES = ['#2a1c14', '#4a2c18', '#e8e2d6', '#7a2f2a'];
const STYLES = ['short', 'long', 'pony', 'bun', 'curly', 'cap', 'short', 'bald'];
const TOPS = ['tee', 'shirt', 'hoodie', 'tie'];

/** Rasgos estables de una persona a partir de su uid. */
export function lookOf(uid) {
    const n = hashId(uid);
    const skin = SKIN[(n >>> 5) % SKIN.length];
    return {
        shirt: SHIRTS[n % SHIRTS.length],
        hair: HAIR[(n >>> 3) % HAIR.length],
        skin,
        pants: PANTS[(n >>> 7) % PANTS.length],
        shoes: SHOES[(n >>> 9) % SHOES.length],
        style: STYLES[(n >>> 11) % STYLES.length],
        top: TOPS[(n >>> 14) % TOPS.length],
        glasses: (n >>> 16) % 4 === 0,
        beard: (n >>> 18) % 6 === 0,
        headphones: (n >>> 20) % 9 === 0,
    };
}

const SIT = { hx: -4, hy: 8, tn: 88, kn: 88, tf: 84, kf: 84 };
const STAND = { tn: 3, kn: 0, tf: -3, kf: 0 };

/**
 * Catálogo de poses: [cuadros, cuadros por segundo, función del cuadro].
 * Ángulos en grados; 0 cuelga hacia abajo y positivo va hacia el frente.
 */
const POSES = {
    idle: [4, 2.2, (f) => ({ ...STAND, sn: 5, en: 8, sf: -5, ef: 8, headY: f === 2 ? 1 : 0, blink: f === 3 })],
    walk: [8, 0, (f) => {
        const phase = (f / 8) * Math.PI * 2;
        const swing = Math.sin(phase);
        return {
            hy: Math.round(Math.abs(swing) * 1.2),
            lean: 3,
            tn: 26 * swing,
            kn: 6 + 38 * Math.max(0, Math.cos(phase)),
            tf: -26 * swing,
            kf: 6 + 38 * Math.max(0, -Math.cos(phase)),
            sn: -24 * swing,
            en: 16,
            sf: 24 * swing,
            ef: 16,
        };
    }],
    walkMug: [8, 0, (f) => ({ ...POSES.walk[2](f), sn: 22, en: 78, held: 'mug' })],
    sit: [2, 1, (f) => ({ ...SIT, sn: 30, en: 40, sf: 26, ef: 40, headY: f })],
    type: [4, 7, (f) => ({ ...SIT, lean: 4, sn: f % 2 ? 52 : 58, en: f % 2 ? 50 : 42, sf: f % 2 ? 60 : 52, ef: f % 2 ? 40 : 50 })],
    sitDrink: [6, 2, (f) => ({ ...SIT, sn: f >= 4 ? 40 : 28, en: f >= 4 ? 138 : 70, sf: 26, ef: 40, held: 'mug', headY: f >= 4 ? -1 : 0 })],
    sitPhone: [4, 3, (f) => ({ ...SIT, sn: 30, en: f % 2 ? 108 : 112, sf: 26, ef: 40, held: 'phone', headY: 1 })],
    sitRead: [6, 1.2, (f) => ({ ...SIT, sn: 34, en: 98, sf: f === 5 ? 44 : 30, ef: f === 5 ? 96 : 90, held: 'book', headY: 1 })],
    sitTalk: [6, 3, (f) => ({ ...SIT, sn: 30 + (f % 3) * 14, en: 60 + (f % 2) * 34, sf: 26, ef: 40, mouth: f % 2 === 0 })],
    sitEat: [6, 2.4, (f) => ({ ...SIT, sn: f % 3 === 2 ? 44 : 34, en: f % 3 === 2 ? 132 : 62, sf: 30, ef: 44, held: 'fork', mouth: f % 3 === 0 })],
    nap: [4, 1, (f) => ({ ...SIT, lean: 10, sn: 24, en: 30, sf: 20, ef: 30, headY: 2 + (f > 1 ? 1 : 0), headX: 1, blink: true })],
    drink: [6, 2, (f) => ({ ...STAND, sn: f >= 4 ? 38 : 22, en: f >= 4 ? 140 : 84, sf: -5, ef: 8, held: 'mug', headY: f >= 4 ? -1 : 0 })],
    use: [4, 5, (f) => ({ ...STAND, lean: 4, sn: f % 2 ? 64 : 72, en: f % 2 ? 34 : 24, sf: f % 2 ? 66 : 58, ef: f % 2 ? 30 : 42 })],
    wash: [4, 6, (f) => ({ ...STAND, lean: 14, sn: f % 2 ? 58 : 64, en: 16, sf: f % 2 ? 62 : 56, ef: 20, headY: 1 })],
    phone: [4, 3, (f) => ({ ...STAND, sn: 24, en: f % 2 ? 112 : 116, sf: -5, ef: 8, held: 'phone', headY: 1 })],
    look: [4, 1.4, (f) => ({ ...STAND, sn: 30, en: 138, sf: -8, ef: 30, headY: f === 1 ? -1 : 0, blink: f === 3 })],
    point: [4, 2, (f) => ({ ...STAND, lean: 3, sn: 104 + (f % 2) * 8, en: 6, sf: -6, ef: 20, mouth: f % 2 === 0 })],
    wave: [4, 6, (f) => ({ ...STAND, sn: 162, en: f % 2 ? 24 : -18, sf: -5, ef: 8, mouth: true })],
    talk: [6, 3.2, (f) => ({ ...STAND, sn: 30 + (f % 3) * 16, en: 60 + (f % 2) * 36, sf: f % 3 === 1 ? 30 : -5, ef: f % 3 === 1 ? 70 : 8, mouth: f % 2 === 0 })],
    play: [4, 8, (f) => ({ ...STAND, lean: 14, tn: 10, tf: -10, sn: f % 2 ? 54 : 66, en: 26, sf: f % 2 ? 70 : 56, ef: 22 })],
    dance: [8, 7, (f) => {
        const beat = f % 4;
        return {
            hy: beat % 2,
            lean: beat < 2 ? 6 : -6,
            tn: beat < 2 ? 14 : -8,
            kn: beat < 2 ? 18 : 4,
            tf: beat < 2 ? -8 : 14,
            kf: beat < 2 ? 4 : 18,
            sn: beat < 2 ? 150 : 44,
            en: 30,
            sf: beat < 2 ? 44 : 150,
            ef: 30,
            mouth: true,
        };
    }],
    cheer: [4, 6, (f) => ({ ...STAND, hy: f % 2 ? -2 : 0, kn: f % 2 ? 0 : 10, kf: f % 2 ? 0 : 10, sn: 160 + (f % 2) * 12, en: 8, sf: 168 - (f % 2) * 12, ef: 8, mouth: true })],
    stretch: [4, 1.6, (f) => ({ ...STAND, lean: -6, sn: 172, en: f % 2 ? 10 : 0, sf: 166, ef: f % 2 ? 0 : 10, blink: true })],
};
POSES.sitLaptop = POSES.type;

function limb(x, y, angle, length) {
    const rad = (angle * Math.PI) / 180;
    return [x + Math.sin(rad) * length, y + Math.cos(rad) * length];
}

function drawHead(grid, look, p, neckX, neckY) {
    const hx = Math.round(neckX) + 1 + (p.headX || 0);
    const top = Math.round(neckY) - 11 + (p.headY || 0);
    const skinDark = shade(look.skin, -34);
    grid.rect(hx - 1, top + 9, 3, 3, skinDark);
    grid.rect(hx - 5, top, 10, 10, look.skin);
    grid.set(hx + 5, top + 6, look.skin);
    grid.set(hx - 2, top + 6, skinDark);
    const hair = look.hair;
    const light = shade(hair, 26);
    if (look.style === 'bald') {
        grid.rect(hx - 5, top + 3, 2, 3, hair);
    } else if (look.style === 'cap') {
        grid.rect(hx - 5, top + 3, 3, 3, hair);
        grid.rect(hx - 5, top, 10, 3, look.shirt);
        grid.rect(hx + 3, top + 2, 5, 1, shade(look.shirt, -30));
    } else {
        grid.rect(hx - 5, top, 10, 3, hair);
        grid.rect(hx - 5, top + 3, 3, 4, hair);
        grid.rect(hx + 2, top + 3, 3, 1, hair);
        grid.rect(hx - 2, top + 1, 4, 1, light);
        if (look.style === 'long') grid.rect(hx - 6, top + 2, 3, 10, hair);
        if (look.style === 'pony') {
            grid.rect(hx - 7, top + 3, 2, 3, hair);
            grid.rect(hx - 8, top + 5, 2, 5, hair);
        }
        if (look.style === 'bun') grid.rect(hx - 6, top - 2, 4, 3, hair);
        if (look.style === 'curly') {
            grid.rect(hx - 6, top - 1, 12, 3, hair);
            grid.rect(hx - 6, top + 2, 3, 6, hair);
            grid.set(hx - 3, top - 2, hair);
            grid.set(hx + 1, top - 2, hair);
            grid.set(hx + 4, top - 2, hair);
        }
    }
    if (p.blink) grid.rect(hx + 2, top + 6, 2, 1, INK);
    else grid.rect(hx + 2, top + 5, 1, 2, INK);
    if (look.beard) grid.rect(hx, top + 7, 5, 3, hair);
    if (p.mouth) grid.rect(hx + 2, top + 8, 2, 1, '#7a2f2a');
    else grid.set(hx + 3, top + 8, shade(look.skin, -60));
    if (look.glasses) {
        grid.rect(hx - 3, top + 5, 4, 1, '#3b4a6b');
        grid.rect(hx + 1, top + 4, 4, 1, '#3b4a6b');
        grid.set(hx + 1, top + 5, '#3b4a6b');
        grid.set(hx + 3, top + 5, '#cfe8ff');
        grid.set(hx + 4, top + 5, '#3b4a6b');
        grid.rect(hx + 1, top + 7, 4, 1, '#3b4a6b');
        grid.set(hx + 3, top + 6, '#cfe8ff');
    }
    if (look.headphones) {
        grid.rect(hx - 3, top - 1, 4, 1, '#22222a');
        grid.rect(hx - 4, top + 3, 3, 4, '#3a3a46');
        grid.set(hx - 3, top + 4, '#e0503c');
    }
}

function drawHeld(grid, held, x, y) {
    const hx = Math.round(x);
    const hy = Math.round(y);
    if (held === 'mug') {
        grid.rect(hx, hy - 4, 3, 4, '#f4efe4');
        grid.rect(hx, hy - 4, 3, 1, '#6b3e2a');
        grid.set(hx + 3, hy - 2, '#d8d0c4');
    } else if (held === 'phone') {
        grid.rect(hx, hy - 5, 2, 5, '#22222a');
        grid.rect(hx, hy - 4, 1, 3, '#8fd0ff');
    } else if (held === 'book') {
        grid.rect(hx - 1, hy - 4, 5, 5, '#b8475f');
        grid.rect(hx, hy - 3, 3, 3, '#f4efe4');
    } else if (held === 'fork') {
        grid.rect(hx + 1, hy - 4, 1, 4, '#c8c8d0');
    }
}

function drawFigure(look, p) {
    const grid = new Grid(W, H);
    const hipX = OX + (p.hx || 0);
    const hipY = OY - 18 + (p.hy || 0);
    const [shX, shY] = limb(hipX, hipY, 180 - (p.lean || 0), TORSO);
    const longSleeve = look.top !== 'tee';
    const farShirt = shade(look.shirt, -38);
    const farPants = shade(look.pants, -26);
    const farSkin = shade(look.skin, -30);

    const leg = (thigh, knee, pants, shoes) => {
        const [kx, ky] = limb(hipX, hipY, thigh || 0, THIGH);
        const [ax, ay] = limb(kx, ky, (thigh || 0) - (knee || 0), SHIN);
        grid.line(hipX, hipY, kx, ky, 4, pants);
        grid.line(kx, ky, ax, ay, 4, pants);
        grid.rect(Math.round(ax) - 2, Math.round(ay), 6, 2, shoes);
        grid.rect(Math.round(ax) - 2, Math.round(ay) - 1, 4, 1, shoes);
    };
    const arm = (shoulder, elbow, shirt, skin, held) => {
        const sx = shX;
        const sy = shY + 3;
        const [ex, ey] = limb(sx, sy, shoulder || 0, ARM);
        const [wx, wy] = limb(ex, ey, (shoulder || 0) + (elbow || 0), ARM);
        grid.line(sx, sy, ex, ey, 3, shirt);
        grid.line(ex, ey, wx, wy, 3, longSleeve ? shirt : skin);
        grid.rect(Math.round(wx) - 1, Math.round(wy) - 1, 3, 3, skin);
        if (held) drawHeld(grid, held, wx + 1, wy);
    };

    arm(p.sf, p.ef, farShirt, farSkin, null);
    leg(p.tf, p.kf, farPants, shade(look.shoes, -20));
    grid.line(hipX, hipY - 3, shX, shY + 3, 7, look.shirt);
    grid.line(hipX - 3, hipY - 3, shX - 3, shY + 3, 1, shade(look.shirt, -24));
    if (look.top === 'tie') grid.line(hipX + 2, hipY - 4, shX + 2, shY + 2, 1, '#8d2f2a');
    if (look.top === 'shirt') grid.line(hipX + 3, hipY - 4, shX + 3, shY + 1, 1, shade(look.shirt, 38));
    if (look.top === 'hoodie') {
        grid.rect(Math.round(shX) - 4, Math.round(shY), 4, 3, shade(look.shirt, -20));
        grid.rect(Math.round(hipX), hipY - 5, 3, 2, shade(look.shirt, -30));
    }
    grid.rect(Math.round(hipX) - 3, hipY - 2, 7, 3, look.pants);
    drawHead(grid, look, p, shX, shY);
    leg(p.tn, p.kn, look.pants, look.shoes);
    arm(p.sn, p.en, look.shirt, look.skin, p.held);
    grid.outline(INK);
    return grid.toCanvas();
}

const cache = new Map();

/** true si la pose deja al avatar sentado. */
export function isSeated(pose) {
    return pose === 'sit' || pose === 'type' || pose === 'nap' || String(pose).indexOf('sit') === 0;
}

/**
 * Dibuja un avatar con los pies en (x, y).
 * `stride` es la distancia recorrida: el paso avanza con ella y no patina.
 */
export function blitAvatar(ctx, x, y, uid, pose, facing, now, stride) {
    const spec = POSES[pose] || POSES.idle;
    const index = spec[1]
        ? Math.floor((now * spec[1]) / 1000 + (hashId(uid) % 7)) % spec[0]
        : Math.floor(Math.abs(stride || 0) / 5.4) % spec[0];
    const key = `${uid}|${pose}|${index}`;
    let sprite = cache.get(key);
    if (!sprite) {
        if (cache.size > 2400) cache.clear();
        sprite = drawFigure(lookOf(uid), spec[2](index));
        cache.set(key, sprite);
    }
    const fx = Math.round(x);
    const fy = Math.round(y);
    ctx.fillStyle = 'rgba(20,16,12,0.22)';
    ctx.fillRect(fx - 15, fy - 2, 30, 4);
    ctx.save();
    ctx.translate(fx, fy);
    if (facing < 0) ctx.scale(-1, 1);
    ctx.drawImage(sprite, -OX * PX, -OY * PX, W * PX, H * PX);
    ctx.restore();
}

/** Hoja con todas las poses de una persona; la usa la vista previa de sprites. */
export function poseNames() {
    return Object.keys(POSES);
}

export function poseFrames(pose) {
    return (POSES[pose] || POSES.idle)[0];
}

/** Dibuja un cuadro concreto, sin reloj. */
export function blitAvatarFrame(ctx, x, y, uid, pose, index) {
    const spec = POSES[pose] || POSES.idle;
    const sprite = drawFigure(lookOf(uid), spec[2](index % spec[0]));
    ctx.drawImage(sprite, Math.round(x) - OX * PX, Math.round(y) - OY * PX, W * PX, H * PX);
}

export const AVATAR_H = 41 * PX;
